import { ProgramComplementaryItem } from '@models/membership/ProgramComplementaryItem'
import { programComplementaryItemSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createProgramComplementaryItem as createProgramComplementaryItemAPI,
  deleteProgramComplementaryItem as deleteProgramComplementaryItemAPI,
  fetchProgramComplementaryItems,
  updateProgramComplementaryItem as updateProgramComplementaryItemAPI,
} from '@services/membership/ProgramComplementaryItemService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<ProgramComplementaryItem> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'programComplementaryItem',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getProgramComplementaryItemsSuccess,
  setQuery: setProgramComplementaryItemQuery,
  reset: resetProgramComplementaryItemState,
  resetQueryAndIds: resetProgramComplementaryItemQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: ProgramComplementaryItem }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectProgramComplementaryItems = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedProgramComplementaryItem = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectProgramComplementaryItemById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectProgramComplementaryItemsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedProgramComplementaryItem =
  (complementaryItem: ProgramComplementaryItem | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<ProgramComplementaryItem>(complementaryItem)
      )
    )

export const getProgramComplementaryItems = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchProgramComplementaryItems(params),
    [programComplementaryItemSchema],
    slice.actions
  )

export const createProgramComplementaryItem = (
  complementaryItem: ProgramComplementaryItem
) =>
  SBAPI.CreateDispatched(
    createProgramComplementaryItemAPI(complementaryItem),
    programComplementaryItemSchema,
    slice.actions
  )

export const updateProgramComplementaryItem = (
  complementaryItem: ProgramComplementaryItem
) =>
  SBAPI.UpdateDispatched<ProgramComplementaryItem>(
    updateProgramComplementaryItemAPI(complementaryItem),
    complementaryItem,
    slice.actions
  )

export const deleteProgramComplementaryItem = (
  complementaryItem: ProgramComplementaryItem
) =>
  SBAPI.DestroyDispatched<ProgramComplementaryItem>(
    deleteProgramComplementaryItemAPI(complementaryItem),
    complementaryItem,
    slice.actions
  )
